/**
 * Declaración de los filtros de la grilla de Personas para <GridFilterPanel />.
 *
 * Vive en su propio módulo por lo mismo que en Facturas y Móviles: lo consumen el panel (para
 * renderizar) y la grilla (para el mapeo a columnas de DataTables y el reset).
 *
 * VEC-3691 fase 1: migración al panel compartido, conservando el tipo de control de cada campo
 * y el query EXACTO que armaba `PersonasGridFiltros::triggerFilter()`.
 * VEC-3691 fase 2: los 5 desplegables simples pasaron a `multiselect`.
 *
 * El query NO cambió de forma: un multiselect con un solo valor serializa igual que el select
 * —`base=7`—, y del lado del backend el filtro parte ese valor por coma y busca con `IN (...)`,
 * que con un elemento es la misma consulta que la igualdad de antes.
 *
 * Los filtros de TEXTO no pasan a múltiples: ahí la coma es un carácter válido de lo que el
 * usuario escribió, y partirla convertiría una búsqueda en dos.
 *
 * A DIFERENCIA DE TICKETS Y MÓVILES, acá NINGÚN filtro está gateado por config ni por permisos:
 * el panel viejo no tenía un solo uso de ConfigBusiness ni de Security. O sea que esta migración
 * NO tiene el cambio observable de "campos que antes se veían siempre y ahora se ocultan".
 */

export const PERSONAS_FILTROS_STORAGE_KEY = 'PersonasGrid_filtros';

/**
 * La grilla de Personas abre SIN filtros del usuario. El "solo activos" no es un default del
 * panel: es el estado natural del check `showInactivos` apagado, y lo aplica la grilla.
 */
export const getPersonasFilterDefaults = () => ({});

/**
 * Migra la persistencia previa a VEC-3691, una sola vez por usuario.
 *
 * *** OJO: EL SHAPE VIEJO ES DISTINTO AL DE TICKETS Y MÓVILES. ***
 * Allá era `{activo: bool, filtros: {...}}` y había que subir un nivel. Acá es PLANO, con el
 * flag mezclado entre los filtros: `{activo: true, apellido: 'x', base: {...}}`. Sacarle `activo`
 * deja exactamente el estado que espera el panel.
 *
 * `activo` era el switch "Persistir Filtros", que la card pide eliminar. Se migra aunque esté en
 * false: la persistencia ahora es siempre, así que lo guardado se recupera igual.
 *
 * Se llama antes de montar el panel, porque el panel lee localStorage en su estado inicial.
 */
export const migrarFiltrosPersistidos = () => {
	try {
		const guardado = window.localStorage.getItem(PERSONAS_FILTROS_STORAGE_KEY);
		if (!guardado) return;

		const parseado = JSON.parse(guardado);
		if (!parseado || parseado.activo === undefined) return;

		delete parseado.activo;
		window.localStorage.setItem(PERSONAS_FILTROS_STORAGE_KEY, JSON.stringify(parseado));
	} catch (e) {}
};

const ANCHO = { xs: 12, sm: 6, md: 3, lg: 3 };

/**
 * Los 11 campos, en el orden en que estaban en el panel viejo.
 *
 * `showInactivos` es el único `check`, y su semántica está INVERTIDA respecto de la columna que
 * filtra: la grilla busca `'1'` en la columna de activos para mostrar SOLO los activos, así que
 * el check prendido tiene que dejar esa búsqueda VACÍA. La inversión la hace la grilla al mapear
 * (ver `PersonasGrid::handleFilterChange`), no el panel: traducir el query a columnas es
 * responsabilidad de la grilla.
 */
export const buildPersonasFilterFields = () => [
	{
		name: 'apellido',
		type: 'text',
		messageId: 'personasGrid.filtros.apellido',
		label: 'Apellido',
		size: ANCHO,
	},
	{
		name: 'nombre',
		type: 'text',
		messageId: 'personasGrid.filtros.nombre',
		label: 'Nombre',
		size: ANCHO,
	},
	{
		name: 'legajoNumero',
		type: 'text',
		messageId: 'personasGrid.filtros.legajo',
		label: 'Legajo',
		size: ANCHO,
	},
	{
		name: 'email',
		type: 'text',
		messageId: 'personasGrid.filtros.email',
		label: 'Email',
		size: ANCHO,
	},
	{
		name: 'documentoNumero',
		type: 'text',
		messageId: 'personasGrid.filtros.documento',
		label: 'Nro. Documento',
		size: ANCHO,
	},
	{
		name: 'base',
		type: 'multiselect',
		messageId: 'personasGrid.filtros.base',
		label: 'Base',
		size: ANCHO,
	},
	{
		name: 'categoria',
		type: 'multiselect',
		messageId: 'personasGrid.filtros.categoria',
		label: 'Categoría',
		size: ANCHO,
	},
	{
		name: 'perfil',
		type: 'multiselect',
		messageId: 'personasGrid.filtros.perfil',
		label: 'Perfil',
		size: ANCHO,
	},
	{
		name: 'region',
		type: 'multiselect',
		messageId: 'personasGrid.filtros.region',
		label: 'Región',
		size: ANCHO,
	},
	{
		name: 'subregion',
		type: 'multiselect',
		messageId: 'personasGrid.filtros.subregion',
		label: 'Sub-Región',
		size: ANCHO,
	},
	{
		name: 'showInactivos',
		type: 'check',
		messageId: 'personasGrid.filtros.ver_inactivos',
		label: 'Ver Inactivos',
	},
];

export default buildPersonasFilterFields;
